import { useCallback, useEffect, useRef, useState } from "react";

import { usePostHog } from "posthog-js/react";
import Pubnub from "pubnub";
import { usePubNub } from "pubnub-react";
import { useSWRConfig } from "swr";

import { ClientAppEvent } from "constants/index";
import {
  revalidateQuoteCompetitivenessScoreCaches,
  revalidateShipmentDetailCaches,
  revalidateShipmentEtaCaches,
  revalidateShipmentQaCaches,
} from "utils";

import { useSwrFetch } from "./useSwrFetch";

import type { ScopedMutator } from "swr";

export type ShipmentUpdateType =
  | "QUOTE_RECEIVED"
  | "QUOTE_UPDATED"
  | "SHIPMENT_PICKED_UP"
  | "SHIPMENT_CANCELLED"
  | "SHIPMENT_DELIVERED"
  | "SHIPMENT_STATE_CHANGED"
  | "SHIPMENT_QA_UPDATED"
  | "QUOTE_QA_UPDATED"
  | "DELIVERY_UPDATED"
  | "ROUTE_ETA_UPDATED"
  | "QUOTE_SCORE_UPDATED";

interface ShipmentLiveUpdateTokenResponse {
  token: string;
  subscribe_key: string;
  channels: string[];
}

interface LiveUpdateSignalPayload {
  update_type: ShipmentUpdateType;
  shipment_id: number;
  actor_role_id: number | null;
  occurred_at: string;
  idempotency_key: string;
}

export interface UseShipmentLiveUpdatesProps {
  shipmentId: number;
  role: Role;
}

export interface UseShipmentLiveUpdatesResult {
  pendingUpdateType: ShipmentUpdateType | null;
  onAccept: () => void;
  onDismiss: () => void;
}

const COALESCE_DEBOUNCE_MS = 1500;

const UPDATE_TYPE_PRIORITY: ShipmentUpdateType[] = [
  "SHIPMENT_CANCELLED",
  "SHIPMENT_DELIVERED",
  "SHIPMENT_STATE_CHANGED",
  "SHIPMENT_PICKED_UP",
  "DELIVERY_UPDATED",
  "ROUTE_ETA_UPDATED",
  "QUOTE_UPDATED",
  "QUOTE_RECEIVED",
  "QUOTE_SCORE_UPDATED",
  "SHIPMENT_QA_UPDATED",
  "QUOTE_QA_UPDATED",
];

const ALL_UPDATE_TYPES = new Set(UPDATE_TYPE_PRIORITY);

export function getHighestPriorityUpdateType(
  types: Set<ShipmentUpdateType>,
): ShipmentUpdateType | null {
  return UPDATE_TYPE_PRIORITY.find((type) => types.has(type)) ?? null;
}

export function resolveRevalidationForUpdateTypes(
  types: Set<ShipmentUpdateType>,
  revalidateAll: ScopedMutator,
  shipmentId: number,
): void {
  revalidateShipmentDetailCaches(revalidateAll, shipmentId);

  if (types.has("SHIPMENT_QA_UPDATED") || types.has("QUOTE_QA_UPDATED")) {
    revalidateShipmentQaCaches(revalidateAll, shipmentId);
  }
  if (types.has("ROUTE_ETA_UPDATED")) {
    revalidateShipmentEtaCaches(revalidateAll, shipmentId);
  }
  if (types.has("QUOTE_SCORE_UPDATED")) {
    revalidateQuoteCompetitivenessScoreCaches(revalidateAll);
  }
}

export function useShipmentLiveUpdates(
  props: UseShipmentLiveUpdatesProps,
): UseShipmentLiveUpdatesResult {
  const { shipmentId, role } = props;
  const sharedPubNub = usePubNub();
  // Ref to avoid re-running the subscription effect on chat token refresh and to read the UUID after auth loads.
  const sharedPubNubRef = useRef(sharedPubNub);
  useEffect(() => {
    sharedPubNubRef.current = sharedPubNub;
  }, [sharedPubNub]);
  const { mutate: revalidateAll } = useSWRConfig();
  const posthog = usePostHog();

  const [pendingUpdateType, setPendingUpdateType] =
    useState<ShipmentUpdateType | null>(null);

  const pendingTypesRef = useRef<Set<ShipmentUpdateType>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bannerShownAtRef = useRef<number | null>(null);

  // Stable refs for values used inside PubNub listener callbacks to avoid
  // re-subscribing when they change between renders.
  const roleIdRef = useRef<number>(role.id);
  const roleTypeRef = useRef(role.type);

  useEffect(() => {
    roleIdRef.current = role.id;
  }, [role.id]);

  useEffect(() => {
    roleTypeRef.current = role.type;
  }, [role.type]);

  const { data: tokenData, refetch: refetchToken } =
    useSwrFetch<ShipmentLiveUpdateTokenResponse>({
      url: `/shipments/${shipmentId}/live-updates-token`,
      errorMessage: "Failed to fetch live updates token",
    });

  // Stable ref for refetchToken so the status listener can call it without
  // being included in the subscription effect's dep array.
  const refetchTokenRef = useRef(refetchToken);
  useEffect(() => {
    refetchTokenRef.current = refetchToken;
  }, [refetchToken]);

  const clearPending = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingTypesRef.current = new Set();
    bannerShownAtRef.current = null;
    setPendingUpdateType(null);
  }, []);

  const onAccept = useCallback(() => {
    const secondsVisible =
      bannerShownAtRef.current !== null
        ? Math.round((Date.now() - bannerShownAtRef.current) / 1000)
        : null;
    posthog?.capture(ClientAppEvent.SHIPMENT_LIVE_UPDATE_REFRESH_ACCEPTED, {
      update_type: pendingUpdateType,
      role_type: role.type,
      seconds_visible: secondsVisible,
    });
    resolveRevalidationForUpdateTypes(
      pendingTypesRef.current,
      revalidateAll,
      shipmentId,
    );
    clearPending();
  }, [
    posthog,
    pendingUpdateType,
    role.type,
    revalidateAll,
    shipmentId,
    clearPending,
  ]);

  const onDismiss = useCallback(() => {
    posthog?.capture(ClientAppEvent.SHIPMENT_LIVE_UPDATE_BANNER_DISMISSED, {
      update_type: pendingUpdateType,
      role_type: role.type,
    });
    clearPending();
  }, [posthog, pendingUpdateType, role.type, clearPending]);

  useEffect(() => {
    if (!tokenData) return undefined;

    const { token, subscribe_key: subscribeKey, channels } = tokenData;

    // Create an isolated PubNub instance so the live-update token does not
    // clobber the shared client's chat authKey.
    const liveUpdateClient = new Pubnub({
      subscribeKey,
      userId: sharedPubNubRef.current.getUUID(),
    });
    liveUpdateClient.setToken(token);
    liveUpdateClient.subscribe({ channels, withPresence: false });

    // Silent revalidation on mount to cover any signals missed before subscribing.
    resolveRevalidationForUpdateTypes(
      ALL_UPDATE_TYPES,
      revalidateAll,
      shipmentId,
    );

    const listener: Pubnub.ListenerParameters = {
      message: (envelope: Pubnub.MessageEvent) => {
        const payload = envelope.message as LiveUpdateSignalPayload;

        // Echo suppression: skip signals triggered by this user's own role.
        if (
          payload.actor_role_id !== null &&
          payload.actor_role_id === roleIdRef.current
        ) {
          return;
        }

        pendingTypesRef.current.add(payload.update_type);

        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          const highestPriority = getHighestPriorityUpdateType(
            pendingTypesRef.current,
          );
          if (highestPriority !== null) {
            bannerShownAtRef.current = Date.now();
            setPendingUpdateType(highestPriority);
            posthog?.capture(ClientAppEvent.SHIPMENT_LIVE_UPDATE_BANNER_SHOWN, {
              update_type: highestPriority,
              role_type: roleTypeRef.current,
            });
          }
        }, COALESCE_DEBOUNCE_MS);
      },
      status: (event: Pubnub.StatusEvent) => {
        if (event.category === "PNReconnectedCategory") {
          // Silent revalidation on reconnect to close the disconnect-window gap.
          resolveRevalidationForUpdateTypes(
            ALL_UPDATE_TYPES,
            revalidateAll,
            shipmentId,
          );
        }
        if (event.category === "PNAccessDeniedCategory") {
          // Self-heal: re-fetch the token and re-subscribe via the SWR refetch.
          refetchTokenRef.current();
        }
      },
    };

    liveUpdateClient.addListener(listener);

    return () => {
      liveUpdateClient.removeListener(listener);
      liveUpdateClient.unsubscribe({ channels });
      liveUpdateClient.stop();
      clearPending();
    };
  }, [tokenData, revalidateAll, posthog, clearPending, shipmentId]);

  return { pendingUpdateType, onAccept, onDismiss };
}
